/* eslint-disable import/prefer-default-export */
import moment from 'moment-timezone';
import { END_OPTION_SPECIFIC_DATE } from '../../../edit-listing/common/config/sessions';
import { getNearestFutureEligibleSessionDate } from './getNearestFutureEligibleSessionDate';
import { getNumberOfCustomSessions } from './getNumberOfCustomSessions';
import calculateCurrentTimeWithOffset from './calculateCurrentTimeWithOffset';

export const getCustomSession = ({
  firstSession,
  numberOfSessions,
  endDate,
  endOption,
  availabilityPlan,
  firstDayOfWeek,
  blockBooking,
  includePast = false,
}) => {
  if (!availabilityPlan) {
    return [];
  }

  const { number, unit } = blockBooking?.ticketSaleClose || {};
  const now = calculateCurrentTimeWithOffset({ number, unit });
  const firstSessionStartDate = firstSession ? moment(firstSession) : moment();

  const firstSessionDay = firstSessionStartDate.format('ddd').toLowerCase();
  const { date: eligibleSessionStartDate } = getNearestFutureEligibleSessionDate({
    date: firstSessionStartDate,
    day: firstSessionDay,
    availabilityPlan,
  });

  // `endDate` is stored as midnight-start of the picked day in London time.
  // Treat it as inclusive of that whole day so the final day's sessions are
  // not dropped by the `isBefore(now)` / `isAfter(end)` checks below.
  const end =
    endOption === END_OPTION_SPECIFIC_DATE && endDate
      ? moment(endDate).tz('Europe/London').endOf('day')
      : null;

  // Use real time here (not ticketSaleClose-offset) — offset is only for per-session past flags.
  // Booking callers rely on [] for an ended term; the term calendar opts in to see its past dates.
  if (!includePast && end?.isBefore(moment())) {
    return [];
  }

  const maxSessions =
    endOption === 'after-sessions' && numberOfSessions
      ? parseInt(numberOfSessions, 10)
      : getNumberOfCustomSessions({
          firstSession: firstSessionStartDate,
          endDate: end,
          endOption,
          availabilityPlan,
          firstDayOfWeek,
        });

  if (maxSessions <= 0) {
    return [];
  }

  const sessions = [];

  let current = eligibleSessionStartDate.clone();

  let count = 0;

  while (count < maxSessions) {
    if (end && current.isAfter(end)) break;

    const { date: eligibleDate, entries } = getNearestFutureEligibleSessionDate({
      date: current,
      day: current.format('ddd').toLowerCase(),
      availabilityPlan,
    });

    current = eligibleDate.clone();

    const formattedDate = eligibleDate.format('ddd Do MMM, YYYY');

    // eslint-disable-next-line no-loop-func
    const allSubSessions = entries.map((entry) => {
      return {
        id: entry.id,
        label: `Session ${count + 1}`,
        date: formattedDate,
        rawDate: eligibleDate.clone().tz('Europe/London'),
        startTime: entry.startTime,
        endTime: entry.endTime,
        past: eligibleDate
          .clone()
          .tz('Europe/London')
          .set({
            hour: entry.startTime.split(':')[0],
            minute: entry.startTime.split(':')[1],
          })
          .isBefore(now),
      };
    });

    sessions.push({
      label: `Session ${count + 1}`,
      date: formattedDate,
      rawDate: eligibleDate.clone().tz('Europe/London'),
      startTime: entries[0].startTime,
      endTime: entries[0].endTime,
      // A day is only "past" when every sub-session on it is past. Deriving
      // this from the first entry alone would incorrectly hide listings whose
      // later block of the day is still in the future.
      past: allSubSessions.every((s) => s.past),
      allSessions: allSubSessions,
    });

    count++;

    // Increment date AFTER adding the session to avoid skipping the first session
    if (count < maxSessions) {
      current = current.clone().add(1, 'day'); // Move to next day
    }
  }

  return sessions;
};
